import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Spinner, Table, Card, Badge, Modal } from 'react-bootstrap'
import { recipeAPI } from '../api/recipeAPI'
import { productionAPI } from '../api/productionAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import PickInventoryModal from '../components/PickInventoryModal'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'

function RecipeExecuteScreen() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { hasPermission, loading: permissionsLoading } = usePermissions()

    const [recipe, setRecipe] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [outputQuantity, setOutputQuantity] = useState(0)
    const [maxQuantity, setMaxQuantity] = useState(0)

    // Inventory picking state
    const [ingredientAllocations, setIngredientAllocations] = useState({})
    // { ingredientId: { inventoryId: quantity, ... }, ... }
    const [showPickModal, setShowPickModal] = useState(false)
    const [selectedIngredient, setSelectedIngredient] = useState(null)

    // Output location state
    const [warehouses, setWarehouses] = useState([])
    const [selectedWarehouse, setSelectedWarehouse] = useState('')
    const [locations, setLocations] = useState([])
    const [selectedLocation, setSelectedLocation] = useState('')
    const [loadingLocations, setLoadingLocations] = useState(false)

    // Output metadata state
    const [outputLot, setOutputLot] = useState('')
    const [outputBbDate, setOutputBbDate] = useState('')

    // Execution state
    const [startingProduction, setStartingProduction] = useState(false)
    const [showMismatchWarning, setShowMismatchWarning] = useState(false)
    const [mismatchDetails, setMismatchDetails] = useState([])
    const [successMessage, setSuccessMessage] = useState(null)

    useEffect(() => {
        fetchRecipe()
        fetchWarehouses()
    }, [id])

    // Fetch locations when warehouse changes
    useEffect(() => {
        if (selectedWarehouse) {
            fetchLocations(selectedWarehouse)
        } else {
            setLocations([])
            setSelectedLocation('')
        }
    }, [selectedWarehouse])

    // Permission check - must be after all hooks
    if (!permissionsLoading && !hasPermission('production.create')) {
        return (
            <Container className="py-4">
                <Alert variant="danger">
                    You don&apos;t have permission to execute recipes. Required: <code>production.create</code>
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate('/recipes')}>
                    Back to Recipes
                </Button>
            </Container>
        )
    }

    const fetchWarehouses = async () => {
        try {
            const data = await warehouseAPI.getAll()
            // Filter to only active warehouses
            const activeWarehouses = Array.isArray(data)
                ? data.filter(w => w.is_active)
                : (data.results || []).filter(w => w.is_active)
            setWarehouses(activeWarehouses)
        } catch (error) {
            console.error('Error fetching warehouses:', error)
        }
    }

    const fetchLocations = async (warehouseId) => {
        try {
            setLoadingLocations(true)
            const data = await locationAPI.getAll(warehouseId)
            // Filter to only active locations
            const activeLocations = Array.isArray(data)
                ? data.filter(l => l.is_active)
                : (data.results || []).filter(l => l.is_active)
            setLocations(activeLocations)

            // Auto-select location ending with "-production" (case-insensitive)
            const productionLoc = activeLocations.find(loc =>
                loc.name.toLowerCase().endsWith('-production')
            )
            if (productionLoc) {
                setSelectedLocation(productionLoc._id)
            } else {
                setSelectedLocation('')
            }
        } catch (error) {
            console.error('Error fetching locations:', error)
            setLocations([])
        } finally {
            setLoadingLocations(false)
        }
    }

    const fetchRecipe = async () => {
        try {
            setLoading(true)
            const data = await recipeAPI.getById(id)
            setRecipe(data)

            try {
                const { max_output } = await recipeAPI.getMaxOutput(id)
                setMaxQuantity(max_output)
                setOutputQuantity(Math.min(data.yield_quantity || 1, max_output))
            } catch (err) {
                console.error('Error fetching max output:', err)
                setMaxQuantity(0)
                setOutputQuantity(0)
            }
        } catch (error) {
            console.error('Error fetching recipe:', error)
            setError('Failed to load recipe data')
        } finally {
            setLoading(false)
        }
    }

    const handleQuantityChange = (value) => {
        const numValue = parseFloat(value) || 0
        setOutputQuantity(Math.min(Math.max(0, numValue), maxQuantity))
    }

    // Calculate required quantity based on output quantity and yield
    const calculateRequiredQuantity = (ingredient) => {
        const outputQty = outputQuantity || 0
        const yieldQty = recipe?.yield_quantity || 1
        const ratio = outputQty / yieldQty
        return (parseFloat(ingredient.actual_quantity) * ratio).toFixed(4)
    }

    // Open pick modal for an ingredient
    const openPickModal = (ingredient) => {
        setSelectedIngredient(ingredient)
        setShowPickModal(true)
    }

    // Handle allocation confirmation from modal
    const handleAllocationConfirm = (allocations) => {
        if (selectedIngredient) {
            setIngredientAllocations(prev => ({
                ...prev,
                [selectedIngredient._id]: allocations
            }))
        }
    }

    // Get total picked quantity for an ingredient
    const getPickedQuantity = (ingredientId) => {
        const allocations = ingredientAllocations[ingredientId] || {}
        return Object.values(allocations).reduce((sum, qty) => sum + (parseFloat(qty) || 0), 0)
    }

    // Check if ingredient has any allocations
    const hasAllocations = (ingredientId) => {
        const allocations = ingredientAllocations[ingredientId] || {}
        return Object.keys(allocations).length > 0
    }

    // Build allocations array from ingredientAllocations state
    const buildAllocationsArray = () => {
        const allocations = []
        for (const [ingredientId, inventoryMap] of Object.entries(ingredientAllocations)) {
            for (const [inventoryItemId, quantity] of Object.entries(inventoryMap)) {
                if (parseFloat(quantity) > 0) {
                    allocations.push({
                        ingredient_id: parseInt(ingredientId),
                        inventory_item_id: parseInt(inventoryItemId),
                        quantity: parseFloat(quantity)
                    })
                }
            }
        }
        return allocations
    }

    // Build the start production payload
    const buildStartProductionPayload = () => {
        return {
            lot_number: outputLot,
            bb_date: outputBbDate,
            target_quantity: parseFloat(outputQuantity),
            production_location_id: parseInt(selectedLocation),
            allocations: buildAllocationsArray()
        }
    }

    // Check for quantity mismatches
    const checkMismatches = () => {
        const mismatches = []
        if (!recipe?.ingredients) return mismatches

        for (const ingredient of recipe.ingredients) {
            const requiredQty = parseFloat(calculateRequiredQuantity(ingredient))
            const pickedQty = getPickedQuantity(ingredient._id)
            const diff = pickedQty - requiredQty

            if (Math.abs(diff) > 0.0001) {
                mismatches.push({
                    ingredientName: ingredient.ingredient_name || ingredient.stock_item_name || ingredient.item_family_name,
                    required: requiredQty.toFixed(4),
                    picked: pickedQty.toFixed(4),
                    difference: diff > 0 ? `+${diff.toFixed(4)}` : diff.toFixed(4),
                    isOver: diff > 0
                })
            }
        }
        return mismatches
    }

    // Validate common fields for both modes
    const validateCommon = () => {
        if (!selectedLocation) {
            setError('Please select an output location')
            return false
        }
        if (outputQuantity <= 0) {
            setError('Output quantity must be greater than 0')
            return false
        }
        if (!outputLot.trim()) {
            setError('Please enter a LOT number')
            return false
        }

        // Check if all ingredients have allocations
        const unallocatedIngredients = recipe?.ingredients?.filter(
            ing => !hasAllocations(ing._id)
        ) || []

        if (unallocatedIngredients.length > 0) {
            const names = unallocatedIngredients.map(
                ing => ing.ingredient_name || ing.stock_item_name || ing.item_family_name
            ).join(', ')
            setError(`Please pick inventory for all ingredients. Missing: ${names}`)
            return false
        }

        return true
    }

    // Validate before start production
    const validateStartProduction = () => {
        if (!validateCommon()) return false

        if (!outputBbDate) {
            setError('Please enter a Best Before date')
            return false
        }

        return true
    }

    const handleConfirmWithMismatch = async () => {
        setShowMismatchWarning(false)
        await startProduction()
    }

    const handleStartProduction = async () => {
        setError(null)
        setSuccessMessage(null)

        if (!validateStartProduction()) {
            return
        }

        // Check for mismatches
        const mismatches = checkMismatches()
        if (mismatches.length > 0) {
            setMismatchDetails(mismatches)
            setShowMismatchWarning(true)
            return
        }

        // No mismatches, proceed directly
        await startProduction()
    }

    const startProduction = async () => {
        setStartingProduction(true)
        setError(null)

        try {
            const payload = buildStartProductionPayload()
            console.log('Starting production with payload:', payload)

            const result = await productionAPI.startProduction(id, payload)

            if (result?.success || result?.production_lot) {
                const productionLot = result.production_lot
                // Navigate to production detail page
                navigate(`/production/${productionLot._id}`)
            } else {
                setError('Failed to start production. Please try again.')
            }
        } catch (error) {
            console.error('Error starting production:', error)
            setError(formatErrorResponse(error) || 'Failed to start production')
        } finally {
            setStartingProduction(false)
        }
    }

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (error) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error}</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/recipes')}>
                    Back to List
                </Button>
            </Container>
        )
    }

    if (!recipe) {
        return (
            <Container className="py-4">
                <Alert variant="warning">Recipe not found</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/recipes')}>
                    Back to List
                </Button>
            </Container>
        )
    }

    const outputProduct = recipe.output_stock_item_data

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Execute Recipe: {recipe.name}</h1>
                </Col>
            </Row>

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}

            <Card className="mb-4">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col xs="auto">
                            {outputProduct?.image ? (
                                <img
                                    src={outputProduct.image}
                                    alt={outputProduct.description}
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    No Image
                                </div>
                            )}
                        </Col>
                        <Col>
                            <h5 className="mb-1">Output Product</h5>
                            <p className="mb-0 text-muted">
                                {outputProduct?.description || 'No output product specified'}
                            </p>
                        </Col>
                        <Col md={5}>
                            <Form.Group controlId="outputQuantity">
                                <Form.Label>Output Quantity</Form.Label>
                                <div className="d-flex align-items-center gap-3">
                                    <Form.Range
                                        min={0}
                                        max={maxQuantity}
                                        step={1}
                                        value={outputQuantity}
                                        onChange={(e) => handleQuantityChange(e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <Form.Control
                                        type="number"
                                        min={0}
                                        max={maxQuantity}
                                        value={outputQuantity}
                                        onChange={(e) => handleQuantityChange(e.target.value)}
                                        style={{ width: '120px', fontSize: '1.5rem', fontWeight: 'bold' }}
                                    />
                                </div>
                                <Form.Text className="text-muted">
                                    Max available: {maxQuantity}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <h4>Required Ingredients</h4>
            {recipe.ingredients && recipe.ingredients.length > 0 ? (
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Ingredient</th>
                            <th>Base Quantity</th>
                            <th>Required Quantity</th>
                            <th>UOM</th>
                            <th style={{ width: '150px' }}>Pick</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recipe.ingredients.map((ingredient) => {
                            const requiredQty = parseFloat(calculateRequiredQuantity(ingredient))
                            const pickedQty = getPickedQuantity(ingredient._id)
                            const isPicked = hasAllocations(ingredient._id)
                            const isComplete = isPicked && Math.abs(pickedQty - requiredQty) < 0.0001
                            const isOver = isPicked && pickedQty > requiredQty
                            const isShort = isPicked && pickedQty < requiredQty

                            return (
                                <tr key={ingredient._id}>
                                    <td>{ingredient.ingredient_name || ingredient.stock_item_name || ingredient.item_family_name}</td>
                                    <td>{parseFloat(ingredient.actual_quantity).toFixed(4)}</td>
                                    <td><strong>{calculateRequiredQuantity(ingredient)}</strong></td>
                                    <td>{ingredient.unit_of_measure}</td>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <Button
                                                variant={isPicked ? 'success' : 'outline-primary'}
                                                size="sm"
                                                onClick={() => openPickModal(ingredient)}
                                            >
                                                {isPicked ? 'Edit' : 'Pick'}
                                            </Button>
                                            {isPicked && (
                                                <>
                                                    {isComplete && (
                                                        <Badge bg="success">
                                                            {pickedQty.toFixed(2)}
                                                        </Badge>
                                                    )}
                                                    {isOver && (
                                                        <Badge bg="warning" text="dark">
                                                            {pickedQty.toFixed(2)} (+)
                                                        </Badge>
                                                    )}
                                                    {isShort && (
                                                        <Badge bg="danger">
                                                            {pickedQty.toFixed(2)} (-)
                                                        </Badge>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
            ) : (
                <Alert variant="info">No ingredients defined for this recipe.</Alert>
            )}

            <Card className="mb-4 mt-4">
                <Card.Header>
                    <h5 className="mb-0">Output Location</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="warehouse">
                                <Form.Label>Warehouse</Form.Label>
                                <Form.Select
                                    value={selectedWarehouse}
                                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                                >
                                    <option value="">Select Warehouse...</option>
                                    {warehouses.map(wh => (
                                        <option key={wh._id} value={wh._id}>
                                            {wh.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="location">
                                <Form.Label>Location</Form.Label>
                                <Form.Select
                                    value={selectedLocation}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                    disabled={!selectedWarehouse || loadingLocations}
                                >
                                    <option value="">
                                        {loadingLocations ? 'Loading...' : 'Select Location...'}
                                    </option>
                                    {locations.map(loc => (
                                        <option key={loc._id} value={loc._id}>
                                            {loc.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Output Metadata</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="outputLot">
                                <Form.Label>LOT Number <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter LOT number"
                                    value={outputLot}
                                    onChange={(e) => setOutputLot(e.target.value)}
                                    autoComplete="off"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="outputBbDate">
                                <Form.Label>Best Before Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={outputBbDate}
                                    onChange={(e) => setOutputBbDate(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Pick Inventory Modal */}
            <PickInventoryModal
                show={showPickModal}
                onHide={() => {
                    setShowPickModal(false)
                    setSelectedIngredient(null)
                }}
                ingredient={selectedIngredient}
                requiredQuantity={selectedIngredient ? parseFloat(calculateRequiredQuantity(selectedIngredient)) : 0}
                recipeId={id}
                onConfirm={handleAllocationConfirm}
                existingAllocations={selectedIngredient ? ingredientAllocations[selectedIngredient._id] : null}
            />

            <div className="mt-4 d-flex gap-2">
                <Button
                    variant="success"
                    onClick={handleStartProduction}
                    disabled={startingProduction || !selectedLocation}
                >
                    {startingProduction ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                            />
                            Starting...
                        </>
                    ) : (
                        'Start Production'
                    )}
                </Button>
                <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/recipes')}
                    disabled={startingProduction}
                >
                    Back to List
                </Button>
            </div>

            {/* Mismatch Warning Modal */}
            <Modal
                show={showMismatchWarning}
                onHide={() => setShowMismatchWarning(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Quantity Mismatch Warning</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-3">
                        The picked quantities do not match the required quantities for some ingredients.
                    </Alert>
                    <Table striped bordered size="sm">
                        <thead>
                            <tr>
                                <th>Ingredient</th>
                                <th>Required</th>
                                <th>Picked</th>
                                <th>Difference</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mismatchDetails.map((m, idx) => (
                                <tr key={idx}>
                                    <td>{m.ingredientName}</td>
                                    <td>{m.required}</td>
                                    <td>{m.picked}</td>
                                    <td className={m.isOver ? 'text-warning' : 'text-danger'}>
                                        {m.difference}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <p className="mb-0 text-muted">
                        Do you want to proceed with execution anyway?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowMismatchWarning(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleConfirmWithMismatch}
                    >
                        Proceed Anyway
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}

export default RecipeExecuteScreen
